/**
 * Keystone API client (scaffold).
 *
 * Mirrors the LWP-Properties session + double-submit-CSRF pattern. The browser
 * holds the session in an HttpOnly cookie; every request is sent with
 * `credentials: "include"` so the cookie is attached, and mutating requests echo
 * the readable `csrftoken` cookie back in the `X-CSRFToken` header so the
 * backend accepts the cookie-authenticated call (double-submit CSRF).
 *
 * This is a stub: it wires the contract the real Django/DRF backend (E0-B*) will
 * satisfy, and degrades gracefully when no backend is running — `getMe()`
 * returns `null` on any 401/network failure so the SPA renders a logged-out
 * shell instead of crashing.
 */

import { resolveTenant } from "../lib/tenant";

const API_BASE = "/api";

const CSRF_COOKIE = "csrftoken";
const CSRF_HEADER = "X-CSRFToken";

// Multi-tenant (E25-8): every API call is scoped to a tenant schema via the
// `X-Tenant` header. The active tenant is resolved at request time from the
// domain (`<tenant>.keystone…`) → a login-pinned hint → a dev fallback of "mac"
// (see lib/tenant.ts), so a SINGLE build serves every tenant. Data endpoints
// require the header; auth endpoints ignore it harmlessly.
const TENANT_HEADER = "X-Tenant";

/** Read a non-HttpOnly cookie value by name (returns "" when absent). */
function readCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name + "=([^;]*)"),
  );
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

/** The signed-in user, as returned by `/api/auth/me/`. */
export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: "agent" | "admin";
  is_active: boolean;
  is_superuser?: boolean;
}

export interface ApiError extends Error {
  status: number;
  /**
   * The DRF `detail` string parsed from the JSON error body, when the
   * response body was JSON and had one (e.g. "Authentication credentials
   * were not provided.", "You do not have permission to perform this
   * action.", "CSRF Failed: ..."). Undefined when the body wasn't JSON or
   * had no `detail` key — callers must not assume it is present (issue
   * #573; #571 deliberately left this out of scope).
   */
  detail?: string;
  /** The parsed JSON error body, when there was one: a refusal that carries what to do next (#635's 409s carry a report and a new token). */
  body?: unknown;
}

function makeApiError(status: number, message: string, detail?: string, body?: unknown): ApiError {
  const err = new Error(message) as ApiError;
  err.status = status;
  if (detail !== undefined) err.detail = detail;
  if (body !== undefined) err.body = body;
  return err;
}

/**
 * True when `err` is an `ApiError` carrying HTTP 403 — a permission denial.
 * No retry can ever turn a 403 into a success, so mutate widgets branch
 * their error copy on this instead of showing retry framing that reads as a
 * system outage (issue #558).
 *
 * NOTE: this is a status-only check, so it is also true for a session-expiry
 * 403 (DRF's `SessionAuthentication` returns 403, not 401, when there is no
 * session at all — see `isSessionExpired` below). Callers that want to show
 * the more specific "sign in again" copy MUST check `isSessionExpired`
 * FIRST (issue #573) — this function's behavior is otherwise unchanged.
 */
export function isPermissionDenied(err: unknown): boolean {
  return err instanceof Error && (err as ApiError).status === 403;
}

/** Shared 403 mutation-error copy (issue #558) — deliberately retry-free. */
export const PERMISSION_DENIED_COPY =
  "You don't have permission for this action — a staff login is required.";

/**
 * A 409 — somebody else got there first. Distinct from a transient failure
 * (issue #599 follow-up): the desk's review queues are worked by two people
 * at once, so "already reviewed by your colleague" is an ordinary outcome,
 * and telling the loser to "try again" is both wrong (a retry 409s too) and
 * the thing that used to double-count her contact attempt.
 */
export function isConflict(err: unknown): boolean {
  return err instanceof Error && (err as ApiError).status === 409;
}

/**
 * DRF `detail` strings that name a missing/invalid session rather than a
 * genuine permission denial. `NotAuthenticated`'s message ("Authentication
 * credentials were not provided.") is the common case — DRF's
 * `SessionAuthentication` returns this on 403 (no session) as readily as on
 * 401 (e.g. token auth), so both statuses are checked in `isSessionExpired`.
 * A permission-denial message (e.g. "You do not have permission to perform
 * this action.") deliberately does NOT match — that stays `isPermissionDenied`.
 *
 * `csrf` is in the list as of #304. Django answers a failed CSRF check with 403
 * `"CSRF Failed: CSRF token missing."` (or "... Origin checking failed ..."),
 * which before this PR was unreachable in the SPA and now is: a privacy
 * extension that clears cookies, or a tab left open past the cookie's life,
 * loses the token while the session cookie survives. The remedy is to reload
 * and sign in again — the same copy as an expired session — whereas the
 * fallback it used to hit, "you don't have permission ... a staff login is
 * required", is both wrong and unactionable.
 */
const SESSION_EXPIRED_DETAIL_PATTERN =
  /authentication credentials|credentials were not provided|not authenticated|csrf/i;

/**
 * True when `err` is a 401 or 403 `ApiError` whose DRF `detail` names a
 * missing/invalid session — as opposed to a genuine permission denial (e.g.
 * the reviewer-role capability gate from #558, which keeps its own
 * `isPermissionDenied`/`PERMISSION_DENIED_COPY` above unchanged). Callers
 * MUST check this before `isPermissionDenied` (issue #573): a 403 with no
 * `detail` at all (or a permission-denial `detail`) is NOT a session
 * expiry, so it falls through to the existing #558 behavior.
 */
export function isSessionExpired(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const apiErr = err as ApiError;
  if (apiErr.status !== 401 && apiErr.status !== 403) return false;
  if (!apiErr.detail) {
    // DRF only returns 401 for an authentication failure (never a
    // permission denial, which is always 403) — so a bare 401 with no body
    // is still safely treated as a session expiry. A bare 403 is ambiguous
    // and stays `isPermissionDenied` territory.
    return apiErr.status === 401;
  }
  return SESSION_EXPIRED_DETAIL_PATTERN.test(apiErr.detail);
}

/** Session-expiry copy (issue #573) — distinct from #558's permission-denied
 * copy above: a permission problem needs a different staff login, but an
 * expired session just needs THIS user to sign back in. */
export const SESSION_EXPIRED_COPY = "Your session has ended — sign in again.";

/**
 * Core fetch wrapper. Always sends the session cookie (`credentials: "include"`)
 * and, for mutating methods, the double-submit CSRF header. Throws an `ApiError`
 * carrying the HTTP status on a non-2xx response.
 */
export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  // Scope every request to the resolved tenant (callers may override per-request).
  if (!headers.has(TENANT_HEADER)) headers.set(TENANT_HEADER, resolveTenant());
  // Default JSON, but NEVER stamp a Content-Type onto a FormData body — the
  // browser must set its own `multipart/form-data; boundary=…` (E13-7 submit).
  if (
    init.body &&
    !(init.body instanceof FormData) &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }
  // Double-submit CSRF for mutating requests.
  if (method !== "GET" && method !== "HEAD") {
    const token = readCookie(CSRF_COOKIE);
    if (token) headers.set(CSRF_HEADER, token);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    method,
    headers,
    credentials: "include",
  });

  if (!response.ok) {
    // Best-effort: parse the DRF `detail` string off the JSON error body so
    // callers can distinguish error flavors (session-expired vs permission-
    // denied vs a genuine failure) that share the same HTTP status (issue
    // #573). Not every non-2xx body is JSON (e.g. a CSRF failure or an
    // upstream proxy error page can be plain text/HTML) — swallow a parse
    // failure and leave `detail` undefined rather than let it mask the
    // original HTTP error.
    let detail: string | undefined;
    let body: unknown;
    try {
      body = await response.json();
      if (body && typeof body === "object" && typeof (body as { detail?: unknown }).detail === "string") {
        detail = (body as { detail: string }).detail;
      }
    } catch {
      // Non-JSON error body — no `detail` to extract.
    }
    throw makeApiError(response.status, `Request failed: ${response.status}`, detail, body);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Fetch the current user. Returns `null` (rather than throwing) when there is no
 * session OR no backend at all, so the app shell renders logged-out gracefully
 * during early scaffold development.
 */
export async function getMe(): Promise<User | null> {
  try {
    return await apiRequest<User>("/auth/me/");
  } catch (err) {
    const status = (err as ApiError).status;
    if (status === 401 || status === 403) return null;
    // Network error / no backend running — treat as logged out.
    return null;
  }
}

/**
 * Authenticate with username + password. On success the backend sets the
 * session cookie and returns the signed-in `User`; on failure this throws an
 * `ApiError` carrying the HTTP status (e.g. 400/401 for bad credentials) so the
 * caller can show an inline error.
 *
 * Login is CSRF-enforced like every other unsafe method (#304) — it is not an
 * exemption and must not be made one, because an unprotected login POST is a
 * login-CSRF hole (an attacker can silently sign a visitor into an account they
 * control). `apiRequest` already sends the `X-CSRFToken` header on every
 * mutating request; the token comes from the `csrftoken` cookie that
 * `GET /api/auth/me/` mints on app boot (`@ensure_csrf_cookie` on `MeView`), so
 * a fresh browser has one before this is ever called.
 */
export async function login(
  username: string,
  password: string,
): Promise<User> {
  return apiRequest<User>("/auth/login/", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

/** Log out the current session (best-effort; clears state either way). */
export async function logout(): Promise<void> {
  try {
    await apiRequest<void>("/auth/logout/", { method: "POST", body: "{}" });
  } catch {
    // Ignore — the caller clears local auth state regardless.
  }
}
